import React, { useState, useEffect } from 'react';
import { Typography, Card, Table, Tag, Button, Modal, Form, Input, Select, message, Space, Checkbox } from 'antd';
import MainLayout from '../../layouts/MainLayout';
import { financialApi } from '../../utils/financialApi';
import ClientSelect from '../../components/common/ClientSelect';
import { extractApiUserMessage } from '../../utils/api';
import { formatPriceToman } from '../../utils/formatPriceToman';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const Refunds = () => {
  const [refunds, setRefunds] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createForm] = Form.useForm();
  const [clientDeals, setClientDeals] = useState([]);
  const [dealsLoading, setDealsLoading] = useState(false);

  const [executeModalVisible, setExecuteModalVisible] = useState(false);
  const [executeLoading, setExecuteLoading] = useState(false);
  const [executeForm] = Form.useForm();
  const [currentRefund, setCurrentRefund] = useState(null);

  const handleClientChange = async (clientId) => {
    createForm.setFieldsValue({ deal_id: undefined });
    setClientDeals([]);
    if (!clientId) return;

    try {
      setDealsLoading(true);
      const res = await financialApi.getClientDeals(clientId);
      const deals = res?.data?.deals || res?.deals || [];
      setClientDeals(Array.isArray(deals) ? deals : []);
    } catch (err) {
      console.error('Failed to load client deals:', err);
      setClientDeals([]);
    } finally {
      setDealsLoading(false);
    }
  };

  const fetchFormData = async () => {
    try {
      const res = await financialApi.getChequeFormData();
      if (res?.data?.bank_accounts) setBankAccounts(res.data.bank_accounts);
    } catch (err) {
      console.error('Failed to load form data:', err);
    }
  };

  const fetchRefunds = async () => {
    try {
      setLoading(true);
      const res = await financialApi.getRefunds();
      const data = res?.data?.refunds?.data || res?.data?.refunds || res?.refunds || [];
      setRefunds(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch refunds:', error);
      setRefunds([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRefunds();
    fetchFormData();
  }, []);

  const handleCreateSubmit = async (values) => {
    try {
      setCreateLoading(true);
      const payload = {
        ...values,
        client_id: Number(values.client_id),
        deal_id: Number(values.deal_id),
        amount: Number(values.amount),
      };
      await financialApi.createRefund(payload);
      message.success('درخواست عودت وجه با موفقیت ثبت شد.');
      setCreateModalVisible(false);
      createForm.resetFields();
      fetchRefunds();
    } catch (error) {
      message.error(extractApiUserMessage(error) || 'خطا در ثبت درخواست');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await financialApi.approveRefund(id);
      message.success('درخواست عودت وجه تایید شد.');
      fetchRefunds();
    } catch (error) {
      message.error(extractApiUserMessage(error) || 'خطا در تایید درخواست');
    }
  };

  const openExecuteModal = (record) => {
    setCurrentRefund(record);
    setExecuteModalVisible(true);
  };

  const handleExecuteSubmit = async (values) => {
    try {
      setExecuteLoading(true);
      const payload = {
        bank_account_id: Number(values.bank_account_id),
      };
      await financialApi.executeRefund(currentRefund.id, payload);
      message.success('پرداخت عودت وجه با موفقیت انجام شد.');
      setExecuteModalVisible(false);
      executeForm.resetFields();
      fetchRefunds();
    } catch (error) {
      message.error(extractApiUserMessage(error) || 'خطا در پرداخت درخواست');
    } finally {
      setExecuteLoading(false);
    }
  };

  const columns = [
    {
      title: 'شناسه',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'مشتری',
      dataIndex: 'client_id',
      key: 'client_id',
      render: (val) => `مشتری #${val}`,
    },
    {
      title: 'معامله',
      dataIndex: 'deal_id',
      key: 'deal_id',
      render: (val) => val ? `معامله #${val}` : '-',
    },
    {
      title: 'مبلغ (تومان)',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => formatPriceToman(amount || 0),
    },
    {
      title: 'دلیل',
      dataIndex: 'reason',
      key: 'reason',
    },
    {
      title: 'وضعیت',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusMap = {
          pending: { color: 'warning', text: 'در انتظار تایید' },
          approved: { color: 'processing', text: 'تایید شده (در انتظار پرداخت)' },
          executed: { color: 'success', text: 'پرداخت شده' },
          rejected: { color: 'error', text: 'رد شده' },
        };
        const st = statusMap[status] || { color: 'default', text: status };
        return <Tag color={st.color}>{st.text}</Tag>;
      },
    },
    {
      title: 'عملیات',
      key: 'action',
      render: (_, record) => (
        <Space>
          {record.status === 'pending' && (
            <Button size="small" type="primary" onClick={() => handleApprove(record.id)}>
              تایید مدیر مالی
            </Button>
          )}
          {record.status === 'approved' && (
            <Button size="small" type="default" onClick={() => openExecuteModal(record)}>
              پرداخت (کسر از بانک)
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="p-4 md:p-6 pb-24 max-w-[1600px] mx-auto animate-fade-in">
        <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <Title level={3} className="m-0 text-gray-800 dark:text-gray-100 font-bold">
              کارتابل عودت وجه (استرداد)
            </Title>
            <Text className="text-gray-500 dark:text-gray-400">
              مدیریت و تایید درخواست‌های عودت وجه به مشتریان
            </Text>
          </div>
          <Button type="primary" onClick={() => setCreateModalVisible(true)}>
            ثبت درخواست جدید
          </Button>
        </div>

        <Card 
          loading={loading}
          bordered={false} 
          className="shadow-sm dark:bg-gray-800 rounded-2xl h-full"
        >
          <Table 
            dataSource={refunds} 
            columns={columns}
            rowKey="id"
            pagination={false}
            size="middle"
            className="custom-table"
            locale={{ emptyText: 'درخواست عودت وجه یافت نشد' }}
          />
        </Card>

        {/* Create Refund Modal */}
        <Modal
          title="ثبت درخواست عودت وجه"
          open={createModalVisible}
          onCancel={() => setCreateModalVisible(false)}
          footer={null}
        >
          <Form form={createForm} layout="vertical" onFinish={handleCreateSubmit}>
            <Form.Item name="client_id" label="مشتری" rules={[{ required: true, message: 'لطفاً مشتری را انتخاب کنید' }]}>
              <ClientSelect
                onChange={handleClientChange}
                placeholder="انتخاب یا جستجوی مشتری (نام یا شماره موبایل)"
                includeInactive
                allowClear
              />
            </Form.Item>
            <Form.Item name="deal_id" label="معامله مربوطه (اختیاری)">
              <Select
                showSearch
                allowClear
                loading={dealsLoading}
                placeholder={
                  dealsLoading
                    ? "در حال دریافت معامله‌ها..."
                    : (clientDeals.length > 0 ? "انتخاب معامله مشتری" : "معامله‌ای برای این مشتری یافت نشد (اختیاری)")
                }
                optionFilterProp="children"
              >
                {clientDeals.map(d => (
                  <Option key={d.id} value={d.id}>
                    {d.name || `معامله #${d.id}`} {d.value ? `(${formatPriceToman(d.value)})` : ''}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="amount" label="مبلغ عودت (تومان)" rules={[{ required: true, message: 'مبلغ الزامی است' }]}>
              <Input type="number" />
            </Form.Item>
            <Form.Item name="reason" label="دلیل عودت" rules={[{ required: true, message: 'دلیل الزامی است' }]}>
              <TextArea rows={3} placeholder="دلیل را وارد کنید..." />
            </Form.Item>
            <Form.Item name="clawback_commission" valuePropName="checked">
              <Checkbox>کسر پورسانت (Clawback)</Checkbox>
            </Form.Item>

            <Form.Item className="mb-0 flex justify-end">
              <Space>
                <Button onClick={() => setCreateModalVisible(false)}>انصراف</Button>
                <Button type="primary" htmlType="submit" loading={createLoading}>ثبت</Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Execute Refund Modal */}
        <Modal
          title="پرداخت و کسر از حساب"
          open={executeModalVisible}
          onCancel={() => setExecuteModalVisible(false)}
          footer={null}
        >
          <Form form={executeForm} layout="vertical" onFinish={handleExecuteSubmit}>
            <Form.Item name="bank_account_id" label="حساب بانکی مبدأ" rules={[{ required: true, message: 'لطفاً حساب بانکی را انتخاب کنید' }]}>
              <Select showSearch placeholder="انتخاب حساب بانکی" optionFilterProp="children">
                {bankAccounts.map(b => (
                  <Option key={b.id} value={b.id}>
                    {b.account_name} - {b.bank_name} ({b.account_number})
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item className="mb-0 flex justify-end">
              <Space>
                <Button onClick={() => setExecuteModalVisible(false)}>انصراف</Button>
                <Button type="primary" htmlType="submit" loading={executeLoading}>پرداخت</Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default Refunds;
